import { useState, useEffect } from 'react'
import { getIdeas, createIdea, updateIdea } from '../services/ideas'
import { ApiErrorException } from '../services/client'
import type { Idea } from '../services/types'
import Modal from '../components/Modal'
import IdeaForm, { type FormData } from '../components/IdeaForm'
import { useToast } from '../components/Toast'

type Status = 'open' | 'partial' | 'done' | 'abandoned'

const columnConfig: Record<Status, { title: string; color: string; accent: string }> = {
  open: { 
    title: 'Open', 
    color: 'var(--energy-cyan)',
    accent: 'rgba(0, 240, 255, 0.15)'
  },
  partial: { 
    title: 'In Progress', 
    color: 'var(--alert-amber)',
    accent: 'rgba(255, 176, 64, 0.15)'
  },
  done: { 
    title: 'Completed', 
    color: 'var(--alert-success)',
    accent: 'rgba(0, 255, 159, 0.15)'
  },
  abandoned: { 
    title: 'Archived', 
    color: 'var(--chrome-400)',
    accent: 'rgba(112, 112, 144, 0.15)'
  },
}

function KanbanView() {
  const [ideas, setIdeas] = useState<Idea[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingIdea, setEditingIdea] = useState<Idea | null>(null)
  const { showSuccess, showError } = useToast()

  useEffect(() => {
    loadIdeas()
  }, [])

  const loadIdeas = async () => {
    setLoading(true)
    setError(null)
    try {
      // KanbanView needs to show all statuses including 'done' and 'abandoned'
      // Fetch ideas for each status explicitly and combine them
      const [openRes, partialRes, doneRes, abandonedRes] = await Promise.all([
        getIdeas({ status: 'open' }),
        getIdeas({ status: 'partial' }),
        getIdeas({ status: 'done' }),
        getIdeas({ status: 'abandoned' }),
      ])
      
      const allIdeas = [
        ...openRes.items,
        ...partialRes.items,
        ...doneRes.items,
        ...abandonedRes.items,
      ]
      
      setIdeas(allIdeas)
    } catch (err) {
      console.error('Failed to load ideas:', err)
      const message = err instanceof ApiErrorException
        ? err.error
        : 'Connection failed. Check network status.'
      setError(message)
      showError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateIdea = async (data: FormData) => {
    try {
      const payload: any = {
        name: data.name,
        rank: data.rank,
      }
      if (data.description) payload.description = data.description
      if (data.category) payload.category = data.category

      await createIdea(payload)
      setIsModalOpen(false)
      showSuccess('Idea initialized successfully')
      loadIdeas()
    } catch (err) {
      const message = err instanceof ApiErrorException
        ? err.error
        : 'Initialization failed. Retry operation.'
      showError(message)
      throw err
    }
  }

  const handleUpdateIdea = async (data: FormData) => {
    if (!editingIdea) return
    try {
      const payload: any = {
        name: data.name,
        rank: data.rank,
        status: data.status,
      }
      if (data.description) payload.description = data.description
      if (data.category) payload.category = data.category

      await updateIdea(editingIdea.id, payload)
      setEditingIdea(null)
      showSuccess('Idea updated successfully')
      loadIdeas()
    } catch (err) {
      const message = err instanceof ApiErrorException
        ? err.error
        : 'Update failed. Retry operation.'
      showError(message)
      throw err
    }
  }

  const handleStatusChange = async (idea: Idea, newStatus: Status) => {
    try {
      await updateIdea(idea.id, { status: newStatus })
      showSuccess(`Status updated to ${columnConfig[newStatus].title}`)
      loadIdeas()
    } catch (err) {
      const message = err instanceof ApiErrorException
        ? err.error
        : 'Status update failed. Retry operation.'
      showError(message)
    }
  }

  const handleEditClick = (idea: Idea) => {
    setEditingIdea(idea)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingIdea(null)
  }

  const getIdeasByStatus = (status: Status): Idea[] => {
    return ideas
      .filter((idea) => idea.status === status)
      .sort((a, b) => b.rank - a.rank)
  }

  const columns: Status[] = ['open', 'partial', 'done', 'abandoned']

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col px-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold tracking-wider mb-1">Workflow Board</h1>
          <p className="text-[var(--chrome-300)] text-sm">
            Drag ideas through the datastream
          </p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="btn-primary flex items-center gap-2"
        >
          <span className="text-lg leading-none">+</span>
          <span>New Idea</span>
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="loading-spinner mb-4" />
          <p className="text-[var(--chrome-300)] text-sm tracking-wider uppercase">
            Loading datastream...
          </p>
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="glass-panel p-8 text-center max-w-md">
            <div className="w-12 h-12 mx-auto mb-4 border-2 border-[var(--alert-coral)] rotate-45 flex items-center justify-center">
              <span className="text-[var(--alert-coral)] text-xl -rotate-45">!</span>
            </div>
            <p className="text-[var(--alert-coral)] mb-4">{error}</p>
            <button onClick={loadIdeas} className="btn-ghost">
              Retry Connection
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 grid grid-cols-4 gap-4 min-h-0">
          {columns.map((status) => {
            const config = columnConfig[status]
            const columnIdeas = getIdeasByStatus(status)
            
            return (
              <div 
                key={status} 
                className="energy-container flex flex-col min-h-0"
              >
                {/* Column Header */}
                <div className="p-4 border-b border-[var(--glass-border)] flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-2 h-2 rotate-45"
                        style={{ 
                          backgroundColor: config.color,
                          boxShadow: `0 0 8px ${config.color}`
                        }}
                      />
                      <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: config.color }}>
                        {config.title}
                      </h2>
                    </div>
                    <span 
                      className="text-xs font-mono px-2 py-0.5 rounded"
                      style={{ 
                        backgroundColor: config.accent,
                        color: config.color
                      }}
                    >
                      {columnIdeas.length}
                    </span>
                  </div>
                </div>

                {/* Column Content */}
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                  {columnIdeas.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                      <p className="text-xs text-[var(--chrome-500)] uppercase tracking-wider">
                        Empty
                      </p>
                    </div>
                  ) : (
                    columnIdeas.map((idea) => (
                      <div
                        key={idea.id}
                        className="crystal-card group"
                      >
                        {/* Card Content */}
                        <div 
                          onClick={() => handleEditClick(idea)}
                          className="p-3 cursor-pointer"
                        >
                          <div className="flex items-start gap-2 mb-2">
                            <span className="rank-indicator text-xs flex-shrink-0">
                              {idea.rank}
                            </span>
                            <h3 className="text-sm font-semibold text-[var(--chrome-100)] group-hover:text-[var(--energy-cyan)] transition-colors line-clamp-2">
                              {idea.name}
                            </h3>
                          </div>

                          {idea.category && (
                            <span className="category-tag text-[10px] mb-2 inline-block">
                              {idea.category}
                            </span>
                          )}
                        </div>

                        {/* Quick Actions */}
                        <div className="px-3 pb-3 pt-0">
                          <div className="flex flex-wrap gap-1.5">
                            {columns
                              .filter((col) => col !== status)
                              .map((targetStatus) => {
                                const targetConfig = columnConfig[targetStatus]
                                return (
                                  <button
                                    key={targetStatus}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleStatusChange(idea, targetStatus)
                                    }}
                                    className="btn-sm btn-ghost text-[10px] px-2 py-1 opacity-60 hover:opacity-100"
                                    style={{ 
                                      '--hover-color': targetConfig.color,
                                    } as React.CSSProperties}
                                  >
                                    → {targetConfig.title}
                                  </button>
                                )
                              })}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Modals */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Initialize New Idea"
      >
        <IdeaForm
          mode="create"
          onSubmit={handleCreateIdea}
          onCancel={handleCloseModal}
        />
      </Modal>

      <Modal
        isOpen={!!editingIdea}
        onClose={handleCloseModal}
        title="Modify Idea Node"
      >
        {editingIdea && (
          <IdeaForm
            mode="edit"
            initialData={editingIdea}
            onSubmit={handleUpdateIdea}
            onCancel={handleCloseModal}
          />
        )}
      </Modal>
    </div>
  )
}

export default KanbanView
